import React from "react";

import { cx, isValidPostCodeFormat, useTranslation } from "utils";

import {
  ACQUISITION_COUNTRY_OPTIONS,
  ACQUISITION_OTHER_COUNTRY,
  ACQUISITION_SOIL_TYPES,
  AcquisitionCountrySelection,
  AcquisitionSoilType,
  WEBSITE_LINK,
  isAcquisitionCountrySupported,
} from "consts";

import { AlertBanner, Anchor, Icon, Input, Option, Select, Typography } from "components";

type LocationStepProps = {
  country: AcquisitionCountrySelection;
  zip: string;
  soilType: AcquisitionSoilType | null;
  onCountryChange: (country: AcquisitionCountrySelection) => void;
  onZipChange: (zip: string) => void;
  onSoilTypeChange: (soilType: AcquisitionSoilType | null) => void;
};

export const isLocationStepComplete = (country: AcquisitionCountrySelection, zip: string, soilType: AcquisitionSoilType | null): boolean => {
  if (!isAcquisitionCountrySupported(country) || !soilType) return false;
  return isValidPostCodeFormat(zip, country);
};

export const LocationStep = ({ country, zip, soilType, onCountryChange, onZipChange, onSoilTypeChange }: LocationStepProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const isCountryUnavailable = !isAcquisitionCountrySupported(country);

  return (
    <div className="soilsight-acquisition-location" data-testid="soilsight-step-location">
      <div className="soilsight-acquisition-location__intro">
        <Typography variant="h2" className="soilsight-acquisition-location__title">
          {t("preLogin.simulation.location.title")}
        </Typography>
        <Typography variant="paragraph-m" className="soilsight-acquisition-location__subtitle">
          {t("preLogin.simulation.location.subtitle")}
        </Typography>
      </div>

      <div className="soilsight-acquisition-location__fields">
        <div className="soilsight-acquisition-field">
          <span className="soilsight-acquisition-field__icon" aria-hidden="true">
            <Icon iconName={isCountryUnavailable ? "tabler/x" : "tabler/check"} size="m" color="white" />
          </span>
          <span className="soilsight-acquisition-field__content">
            <span className="soilsight-acquisition-field__label">{t("preLogin.simulation.location.countryLabel")}</span>
            <Select
              className="soilsight-acquisition-field__select"
              value={country}
              dataTestId="soilsight-location-country"
              showSearch
              popupMatchSelectWidth
              filterOption={(input, option) => (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())}
              onChange={(nextCountry) => onCountryChange(nextCountry as AcquisitionCountrySelection)}
            >
              {ACQUISITION_COUNTRY_OPTIONS.map((countryOption) => (
                <Option key={countryOption} value={countryOption} data-testid={`soilsight-location-country-option-${countryOption}`}>
                  {countryOption === ACQUISITION_OTHER_COUNTRY ? t("preLogin.simulation.location.countries.OTHER") : t(`enums:countries.${countryOption}`)}
                </Option>
              ))}
            </Select>
          </span>
        </div>

        {isCountryUnavailable ? (
          <AlertBanner
            type="info"
            iconName="tabler/info-circle"
            iconSize={24}
            dataTestId="soilsight-location-country-error"
            className="soilsight-acquisition-location__country-error"
            description={
              <Typography variant="paragraph-m">
                {t("preLogin.simulation.location.countryUnavailable")}{" "}
                <Anchor href={WEBSITE_LINK} target="_blank">
                  klim.eco
                </Anchor>
              </Typography>
            }
          />
        ) : null}

        <Input
          className="soilsight-acquisition-input"
          label={t("preLogin.simulation.location.zipLabel")}
          placeholder={t("preLogin.simulation.location.zipPlaceholder")}
          inputMode="numeric"
          autoComplete="postal-code"
          prefix={
            <span className={cx("soilsight-acquisition-field__icon", isCountryUnavailable && "soilsight-acquisition-field__icon--muted")} aria-hidden="true">
              <Icon iconName="tabler/map-pin" size="m" color="white" />
            </span>
          }
          value={zip}
          dataTestId="soilsight-location-zip"
          onChange={(event) => onZipChange(event.target.value)}
        />

        <div className="soilsight-acquisition-field">
          <span className={cx("soilsight-acquisition-field__icon", isCountryUnavailable && "soilsight-acquisition-field__icon--muted")} aria-hidden="true">
            <Icon iconName="klim/field-apply" size="m" color="white" />
          </span>
          <span className="soilsight-acquisition-field__content">
            <span className="soilsight-acquisition-field__label">{t("preLogin.simulation.location.soilLabel")}</span>
            <Select
              className="soilsight-acquisition-field__select"
              value={soilType ?? ""}
              dataTestId="soilsight-location-soil"
              onChange={(nextSoilType) => onSoilTypeChange((nextSoilType || null) as AcquisitionSoilType | null)}
            >
              <Option value="" disabled>
                {t("preLogin.simulation.location.soilPlaceholder")}
              </Option>
              {ACQUISITION_SOIL_TYPES.map((soilTypeOption) => (
                <Option key={soilTypeOption} value={soilTypeOption} data-testid={`soilsight-location-soil-option-${soilTypeOption}`}>
                  {t(`preLogin.simulation.location.soilTypes.${soilTypeOption}`)}
                </Option>
              ))}
            </Select>
          </span>
        </div>
      </div>
    </div>
  );
};
